const VP_CART_QTY_DEBOUNCE_MS = 300;

const CartAPI = {
  jsonHeaders: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },

  async request(url, options = {}) {
    const response = await fetch(url, options);

    if (!response.ok) {
      throw new Error(`Cart API request failed (${response.status})`);
    }

    return response.json();
  },

  getCart() {
    const url = `${window.routes?.cart_url || '/cart'}.js`;
    return this.request(url, { headers: { Accept: 'application/json' } });
  },

  async addItem(variantId, quantity = 1, properties = null) {
    const url = `${window.routes?.cart_add_url || '/cart/add'}.js`;
    const item = { id: variantId, quantity };

    if (properties && typeof properties === 'object') {
      const cleaned = {};
      Object.entries(properties).forEach(([key, value]) => {
        if (value == null || value === '') return;
        cleaned[key] = String(value);
      });
      if (Object.keys(cleaned).length) {
        item.properties = cleaned;
      }
    }

    await this.request(url, {
      method: 'POST',
      headers: this.jsonHeaders,
      body: JSON.stringify({
        items: [item],
      }),
    });

    return this.getCart();
  },

  async changeQuantity(lineKey, quantity) {
    const url = `${window.routes?.cart_change_url || '/cart/change'}.js`;

    await this.request(url, {
      method: 'POST',
      headers: this.jsonHeaders,
      body: JSON.stringify({
        id: lineKey,
        quantity,
      }),
    });

    return this.getCart();
  },

  async removeItem(lineKey) {
    return this.changeQuantity(lineKey, 0);
  },

  async updateCart(payload) {
    const url = `${window.routes?.cart_update_url || '/cart/update'}.js`;

    // Use the update.js response directly — a follow-up getCart() can briefly
    // return stale discount_codes after clearing.
    return this.request(url, {
      method: 'POST',
      headers: this.jsonHeaders,
      body: JSON.stringify(payload),
    });
  },

  async clearDiscount() {
    const updateUrl = `${window.routes?.cart_update_url || '/cart/update'}.js`;
    const root = window.Shopify?.routes?.root || '/';

    let cart = await this.request(updateUrl, {
      method: 'POST',
      headers: this.jsonHeaders,
      body: JSON.stringify({ discount: '' }),
    });

    const stillApplied = () =>
      typeof window.vpCollectDiscountCodes === 'function'
        ? window.vpCollectDiscountCodes(cart).length > 0
        : (cart?.discount_codes || []).some((entry) => entry?.applicable === true);

    if (!stillApplied()) return cart;

    // Some storefronts ignore empty JSON strings; form-urlencoded is more reliable.
    const formBody = new URLSearchParams();
    formBody.set('discount', '');
    cart = await this.request(updateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        Accept: 'application/json',
      },
      body: formBody.toString(),
    });

    if (!stillApplied()) return cart;

    // Last resort: applying a guaranteed-invalid code clears the discount session.
    await fetch(`${root}discount/CLEAR_VP_DISCOUNT`, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    }).catch(() => {});

    return this.getCart();
  },
};

class CartState extends EventTarget {
  #cart = null;
  #lastAddedItem = null;

  get cart() {
    return this.#cart;
  }

  get lastAddedItem() {
    return this.#lastAddedItem;
  }

  setCart(cart, { lastAddedVariantId } = {}) {
    this.#cart = cart;

    if (lastAddedVariantId != null) {
      // Cart.js items are newest-first; fall back to items[0] (last added).
      this.#lastAddedItem =
        cart.items.find((item) => String(item.variant_id) === String(lastAddedVariantId)) ||
        cart.items[0] ||
        null;
    }

    this.dispatchEvent(
      new CustomEvent('cart:change', {
        detail: {
          cart: this.#cart,
          lastAddedItem: this.#lastAddedItem,
        },
      })
    );
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.state = new CartState();
    this.upsell = null;
    this.totals = null;
    this.onOverlayClick = this.close.bind(this);
    this.changeQuantityDebounced = debounce((lineKey, quantity) => {
      return this.mutateCart(() => CartAPI.changeQuantity(lineKey, quantity));
    }, VP_CART_QTY_DEBOUNCE_MS);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Checkout')) {
        this.handleCheckout(event);
      }
    });
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.upsell = new VpCartUpsell(this);
    this.totals = new VpCartTotals(this);
    this.bindOverlay();

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-drawer' || event.source === 'product-form') return;

      this.syncCartState({
        lastAddedVariantId: event.productVariantId || event.variantId,
      });
    });

    this.stateUnsubscriber = () => {
      this.renderUpsell();
      this.renderTotals();
    };
    this.state.addEventListener('cart:change', this.stateUnsubscriber);

    this.syncCartState();
    ProductData.prefetchFromConfig();
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
    if (this.stateUnsubscriber) {
      this.state.removeEventListener('cart:change', this.stateUnsubscriber);
    }
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;

    overlay.removeEventListener('click', this.onOverlayClick);
    overlay.addEventListener('click', this.onOverlayClick);
  }

  handleCheckout(event) {
    event.preventDefault();

    const button = event.target.closest('#CartDrawer-Checkout');
    if (button?.disabled || this.classList.contains('is-empty')) return;

    const root = window.Shopify?.routes?.root || '/';
    window.location.href = `${root}checkout`;
  }

  async syncCartState(options = {}) {
    try {
      const cart = await CartAPI.getCart();
      this.state.setCart(cart, options);
      this.updateCartCount(cart.item_count);
      this.classList.toggle('is-empty', !cart.item_count);
    } catch (error) {
      console.error('[cart-drawer] Failed to load cart', error);
    }
  }

  async mutateCart(apiCall, options = {}) {
    const cart = await apiCall();
    this.state.setCart(cart, options);
    this.updateCartCount(cart.item_count);
    this.classList.toggle('is-empty', !cart.item_count);

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'cart-drawer',
      cartData: cart,
    });

    return cart;
  }

  renderUpsell() {
    if (!this.upsell) return;

    const groups = UpsellEngine.getVisibleGroups(this.state.cart);
    this.upsell.ensureSelectionDefaults(groups);
    const handles = groups
      .map((group) => this.upsell.getSelectedProduct(group)?.handle)
      .filter(Boolean);

    ProductData.prefetch(handles).finally(() => {
      this.upsell.render();
    });
  }

  renderTotals() {
    this.totals?.render();
  }

  replaceDrawerInner(html) {
    if (!html) return false;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newInner = doc.querySelector('.drawer__inner');
    const currentInner = this.querySelector('.drawer__inner');
    if (!newInner || !currentInner) return false;

    currentInner.replaceWith(newInner);
    this.bindOverlay();
    return true;
  }

  async refreshDrawerInner() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}?sections=cart-drawer`);

    if (!response.ok) {
      throw new Error(`Failed to refresh cart drawer (${response.status})`);
    }

    const sections = await response.json();
    return this.replaceDrawerInner(sections['cart-drawer']);
  }

  async addUpsellVariant(variantId, properties = null) {
    await this.mutateCart(() => CartAPI.addItem(variantId, 1, properties), {
      lastAddedVariantId: variantId,
    });
    await this.refreshDrawerInner();
    this.renderUpsell();
    this.renderTotals();
  }

  updateCartCount(itemCount) {
    const countEl = this.querySelector('[data-vp-cart-count]');
    if (countEl) countEl.textContent = `(${itemCount})`;

    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    let headerCount = cartLink.querySelector('.verti-header__cart-count');

    if (itemCount > 0) {
      if (!headerCount) {
        headerCount = document.createElement('span');
        headerCount.className = 'verti-header__cart-count';
        headerCount.setAttribute('aria-hidden', 'true');
        cartLink.appendChild(headerCount);
      }

      headerCount.textContent = itemCount;
    } else if (headerCount) {
      headerCount.remove();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy, { sync = true } = {}) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    if (sync) {
      this.syncCartState();
      ProductData.prefetchFromConfig();
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden', 'vp-cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'vp-cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    this.productId = parsedState.id;
    this.classList.remove('is-empty');

    const sectionHtml = parsedState.sections?.['cart-drawer'];
    let replaced = sectionHtml ? this.replaceDrawerInner(sectionHtml) : false;

    if (!replaced) {
      await this.refreshDrawerInner();
    }

    await this.syncCartState({ lastAddedVariantId: parsedState.id });
    this.renderUpsell();
    this.renderTotals();
    this.open(this.activeElement, { sync: false });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // ATC + drawer mutations already refresh the panel — avoid racing section swaps.
      if (
        event.source === 'cart-items' ||
        event.source === 'product-form' ||
        event.source === 'cart-drawer'
      ) {
        return;
      }

      return this.onCartUpdate();
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.add('cart__items--disabled');

    document.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`).forEach((spinner) => {
      spinner.classList.remove('hidden');
    });

    document.activeElement?.blur();
    this.lineItemStatusElement?.setAttribute('aria-hidden', 'false');
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.remove('cart__items--disabled');

    document.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`).forEach((spinner) => {
      spinner.classList.add('hidden');
    });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

window.VPCartAPI = CartAPI;
window.VPCartState = CartState;
